import { useEffect, useId, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Button, Screen, TextField } from '../../ui';
import { useSession } from '../../state/useSession';
import { EMAIL_SHAPE, keepEarlyEmail } from '../../state/earlyEmail';
import { useWhat } from '../../state/what';
import { composePrompt, TYPES, type Answer, type Question } from '../../journey/questions';
import { assignedVariant } from '../../analytics/experiment';
import { funnelStep } from '../../analytics/funnel';
import { usePlanRequest } from './planRequest';

/** Said under the last button in both groups: the wait, and that they need not sit through it. */
export const ABOUT_FOUR_MINUTES = 'About 4 minutes. We can email you when it’s ready.';

/**
 * /new/questions — V7's questions for the type chosen on "What is it?" (journey/questions.ts).
 *
 *  a: "A few questions", all on one screen (V7-A/03-questions), and "Build my tracker" builds:
 *     the plan is made from their answers and built by itself, with no plan screens.
 *  b: one question per screen with step bars and Back / Next (V7-B/03…06), then the plan's own
 *     screens (features, look, review), where they press Build.
 *
 * Their answers go into the prompt as plain lines under their sentence (composePrompt).
 */
export default function Questions() {
  const navigate = useNavigate();
  const what = useWhat();
  const [group] = useState<'a' | 'b'>(() => assignedVariant('v7_flow') ?? 'a');
  const { waiting, request } = usePlanRequest();
  const { isAuthenticated, isInitializing } = useSession();
  // "Where should we send it?" (owner, 2026-11-01): signed out only, optional, and Skip puts it away.
  const askEmail = !isInitializing && !isAuthenticated;
  const [email, setEmail] = useState('');
  const [emailSkipped, setEmailSkipped] = useState(false);
  const [emailProblem, setEmailProblem] = useState<string | null>(null);
  const def = TYPES[what.type];
  const steps = def.b;
  const at = Math.min(what.step, steps.length - 1);
  const stepId = group === 'a' ? 'all' : steps[at].id;
  // Once per screen shown: b's screens each count, Back to one counts it again.
  const hasPrompt = Boolean(what.prompt.trim());
  useEffect(() => {
    if (hasPrompt) funnelStep('questions_viewed', { type: what.type, step: stepId });
  }, [hasPrompt, what.type, stepId]);

  // Answers for this type and group, kept as given (a reload here, or Back from the plan).
  useEffect(() => {
    useWhat.getState().ensureAnswers(group);
  }, [group, what.type]);

  // Nothing said yet (a reload in another tab, an old link): the sentence comes first.
  if (!what.prompt.trim()) return <Navigate to="/new/what" replace />;

  /** The address, kept for the build to send to; false when what was typed is not one. */
  function takeEmail(): boolean {
    if (!askEmail) return true;
    const address = email.trim();
    if (emailSkipped || !address) {
      funnelStep('early_email', { skipped: 'yes' });
      return true;
    }
    if (!EMAIL_SHAPE.test(address)) {
      setEmailProblem('Enter your full email, like you@example.com, or press Skip');
      return false;
    }
    keepEarlyEmail(address);
    funnelStep('early_email', { skipped: 'no' });
    return true;
  }

  function build(): void {
    if (!takeEmail()) return;
    const now = useWhat.getState();
    funnelStep('questions_answered', { type: now.type });
    request(composePrompt(now.prompt, now.type, group, now.answers), { auto: group === 'a' });
  }

  const set = (id: string, value: Answer): void => useWhat.getState().answer(id, value);

  const sendTo = askEmail && !emailSkipped ? (
    <div className="questions-email">
      <TextField
        label="Where should we send it?"
        type="email"
        value={email}
        onChangeText={(v) => { setEmail(v); setEmailProblem(null); }}
        autoComplete="email"
        inputMode="email"
        placeholder="you@example.com"
        error={emailProblem ?? undefined}
      />
      <p className="type-caption questions-email-note">
        Optional. We’ll email you the link the moment it’s built.{' '}
        <button type="button" className="link-button" onClick={() => { setEmailSkipped(true); setEmailProblem(null); }}>Skip</button>
      </p>
    </div>
  ) : null;

  if (group === 'a') {
    return (
      <Screen>
        <div className="questions">
          <h1 className="type-display questions-title">A few questions</h1>
          {def.a.map((q) => <QuestionField key={q.id} q={q} value={what.answers[q.id]} onChange={(v) => set(q.id, v)} />)}
          {sendTo}
        </div>
        <div className="questions-actions">
          <Button title={waiting ? 'Getting it ready…' : def.build} loading={waiting} onPress={build} />
          <p className="type-body questions-note">{ABOUT_FOUR_MINUTES}</p>
        </div>
      </Screen>
    );
  }

  const step = steps[at];
  const last = at === steps.length - 1;
  // No step bars or "step 2 of 7" (owner, 2026-10-19): B's visitors aren't told where they are or how much is left.
  return (
    <Screen>
      <div className="questions questions-b">
        <h1 className="type-display questions-title">{step.title}</h1>
        {step.sub && <p className="type-body questions-sub">{step.sub}</p>}
        {step.questions.map((q) => <QuestionField key={q.id} q={q} value={what.answers[q.id]} onChange={(v) => set(q.id, v)} />)}
        {last && sendTo}
      </div>
      <div className="questions-actions questions-actions-b">
        <div className="questions-nav">
          <button
            type="button"
            className="questions-back"
            onClick={() => (at === 0 ? navigate('/new/what') : useWhat.getState().setStep(at - 1))}
          >
            Back
          </button>
          <Button
            title={waiting ? 'Getting it ready…' : 'Next'}
            loading={waiting}
            onPress={() => {
              if (last) build();
              else {
                useWhat.getState().setStep(at + 1);
                // The next screen from its top, not scrolled to where the last one's Next was.
                document.documentElement.scrollTop = 0;
              }
            }}
          />
        </div>
      </div>
    </Screen>
  );
}

/** One question, drawn by its kind. */
function QuestionField({ q, value, onChange }: { q: Question; value: Answer | undefined; onChange: (v: Answer) => void }) {
  const id = useId();
  const heading = q.label ? <h2 className="type-heading questions-ask" id={id}>{q.label}</h2> : null;

  if (q.kind === 'chips') {
    const picked = Array.isArray(value) ? value : q.defaults;
    return (
      <section className="questions-block">
        {heading}
        <div className="questions-chips" role="group" aria-labelledby={heading ? id : undefined} aria-label={heading ? undefined : 'Choices'}>
          {q.options.map((o) => {
            const on = picked.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={on}
                className={on ? 'questions-chip is-on' : 'questions-chip'}
                onClick={() => onChange(on ? picked.filter((v) => v !== o.value) : [...picked, o.value])}
              >
                <span className="questions-chip-mark" aria-hidden="true">{on ? '✓' : '+'}</span>
                {o.label}
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  if (q.kind === 'checks') {
    const picked = Array.isArray(value) ? value : q.defaults;
    const full = q.max !== undefined && picked.length >= q.max;
    return (
      <section className="questions-block">
        <div className="questions-checks-head">
          {q.label && <p className="type-body questions-sub questions-checks-label" id={id}>{q.label}</p>}
          {q.max !== undefined && <span className="questions-count" aria-live="polite">{picked.length} of {q.max}</span>}
        </div>
        <div className="questions-rows" role="group" aria-labelledby={q.label ? id : undefined}>
          {q.options.map((o) => {
            const on = picked.includes(o.value);
            return (
              <label key={o.value} className={on ? 'questions-row is-on' : full ? 'questions-row is-off' : 'questions-row'}>
                <input
                  type="checkbox"
                  className="questions-box"
                  checked={on}
                  disabled={!on && full}
                  onChange={() => onChange(on ? picked.filter((v) => v !== o.value) : [...picked, o.value])}
                />
                <span className="questions-row-text"><span className="questions-row-label">{o.label}</span></span>
              </label>
            );
          })}
        </div>
      </section>
    );
  }

  if (q.kind === 'choice') {
    const picked = typeof value === 'string' ? value : '';
    return (
      <section className="questions-block">
        {heading}
        <div className="questions-chips" role="radiogroup" aria-labelledby={heading ? id : undefined}>
          {q.options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={picked === o.value}
              className={picked === o.value ? 'questions-chip questions-pick is-on' : 'questions-chip questions-pick'}
              // A second tap takes it back: these are optional.
              onClick={() => onChange(picked === o.value ? '' : o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </section>
    );
  }

  if (q.kind === 'radio') {
    const picked = typeof value === 'string' ? value : q.default;
    return (
      <section className="questions-block">
        {heading}
        <div className="questions-rows" role="radiogroup" aria-labelledby={heading ? id : undefined} aria-label={heading ? undefined : 'Choose one'}>
          {q.options.map((o) => (
            <label key={o.value} className={picked === o.value ? 'questions-row is-on' : 'questions-row'}>
              <input
                type="radio"
                className="questions-radio"
                name={`${id}-${q.id}`}
                checked={picked === o.value}
                onChange={() => onChange(o.value)}
              />
              <span className="questions-row-text">
                <span className="questions-row-label">{o.label}</span>
                {o.sub && <span className="questions-row-sub">{o.sub}</span>}
              </span>
            </label>
          ))}
        </div>
      </section>
    );
  }

  if (q.kind === 'toggle') {
    const on = typeof value === 'boolean' ? value : q.default;
    return (
      <section className="questions-block">
        <label className="questions-row questions-toggle">
          <span className="questions-row-text"><span className="questions-row-label">{q.label}</span></span>
          <input type="checkbox" role="switch" className="questions-switch" checked={on} onChange={() => onChange(!on)} />
        </label>
      </section>
    );
  }

  if (q.kind === 'date') {
    return (
      <section className="questions-block">
        <label className="type-caption questions-field-label" htmlFor={id}>{q.label}</label>
        <input id={id} type="date" className="field questions-input" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
      </section>
    );
  }

  return (
    <section className="questions-block">
      {q.label ? (
        <label className="type-caption questions-field-label" htmlFor={id}>{q.label}</label>
      ) : null}
      <input
        id={id}
        className="field questions-input"
        value={typeof value === 'string' ? value : ''}
        maxLength={120}
        placeholder={q.placeholder}
        aria-label={q.label ? undefined : q.placeholder.replace(/^e\.g\.\s*/, '')}
        onChange={(e) => onChange(e.target.value)}
      />
    </section>
  );
}
